import _ from 'lodash'
import Backbone from 'backbone'
import ReportStack from './report-stack'
import reportConstants from 'scoreboard/reports/report-constants'
import i18n from 'scoreboard/components/i18n'
import DataType from 'scoreboard/datasets/model/data-type'
const { DATE, DATE_TIME, DATASET_LINK } = DataType

function hasNoTransientModels(models) {
	return _.every(models, m => !!m.get('transient') == false)
}

/**
 * A Collection of stacks (i.e. ReportStack) for a report
 */
class Stacks extends Backbone.Collection {

	initialize(model, opts = {}) {
		this.options = opts
		this.reportModel = opts.reportModel

		//all sorting needs to be done on the server
		this._refetchOnChange(['sortType'], (prop, model, value, opts) => {
			//when updating the sort on a column not in a group, we unset the sort
			//on any other non-grouped column because we currently only support sorting
			//on one column within a group.
			//sorting on the group columns themselves are different because the
			//sorting on the group itself happens independently of the results within the group
			//therefore groupingSortType is not affected by changes in the un-grouped
			//sort.
			if (this._sortedColumn && this._sortedColumn !== model) {
				if (this._sortedColumn.get('sortType') == 'tree') {
					//remove the indent if the previous column was sorting by tree
					this._sortedColumn.unset('indentHierarchy', {silent: true})
				}
				this._sortedColumn.unset('sortType', {silent: true})
			}
			if (value != 'tree' && !!model.get('indentHierarchy')) {
				//remove the indent if we're no longer sorting by tree
				model.unset('indentHierarchy', {silent: true})
			}
			this._sortedColumn = model
			return true
		})
		this._refetchOnChange(['groupingSortType'], _.stubTrue)

		//refetch if we enable indenting because we may need to calculate depths
		this._refetchOnChange('indentHierarchy', (prop, model, value) => !!value)

		//refetch if calculated equation changed (and the column is calculated)
		this._refetchOnChange('equation', (prop, model, value, opts) => {
			return model.get('calculated') && this._isPreviousNotEqual(prop, model, value)
		})

		//#37784 changing the dataset aggregations/filters for datasets requires a fetch
		this._refetchOnChange(['datasetAggregations', 'datasetFilters'])

		//refetch if we're changing the breakdown
		this._refetchOnChange('breakdowns', _.stubTrue)

		//refetch if we're changing the breakdown's sort value or it's sort aggregation type
		this._refetchOnChange(['sortByValue','sortAggregationType'], _.stubTrue)

		//refetch if the aggregation changes
		this._refetchOnChange('aggregationType', _.stubTrue)

		//refetch if we need scoring color information
		this._refetchOnChange('colorType', (prop, model, value, opts) => {
			return value == reportConstants.COLOR_TYPE_ID_SCORING
		})

		//every time the layout changes, if any new equations detected, refetch data
		this._refetchOnChange('advancedLayout', (prop, model, layout, opts) => {
			return _.difference(model._currentCalculatedEquations, model._previousCalculatedEquations).length > 0
		})

		//if the a repeated stack range/breakdown by is changed or the order changed
		//we need to refetch
		this._refetchOnChange(['range', 'breakdownBy', 'headerOrder'], _.stubTrue)

		//if the note display options type or range changes, we'll need to refetch
		this._refetchOnChange('noteDisplayOptions', (prop, model, value, opts) => {
			let prev = model.previous(prop)
			return _.isNil(prev) || prev.displayType != value.displayType
				|| !_.isEqual(prev.range, value.range)
		})

		//#36393 dataset-link values need to be fetched from the server
		this._refetchOnChange('dataType', (prop, model, value, opts) => {
			return value == DATASET_LINK
		})

		this._refetchOnChange('repeatBy', (prop, model, value, opts) => {
			//if we're adding a new repeat by or deleting a repeat by fetch the
			//repeating data
			return (value && _.isNil(model.previous(prop))) ||
				(!_.isNil(model.previous(prop)) && _.isNil(value))
		})

		//#38052 always refetch when we're changing a dataset field
		this._refetchOnChange('datasetFieldId', (prop, model, value, opts) => {
			this._validateColumnState(model)
			return true
		})

		//refetch if we're changing a grouped column's type
		this._refetchOnChange('columnType', (prop, model, value, opts) => {
			this._validateColumnState(model)
			return !_.isNil(model.get('groupingId'))
		})

		// #39394
		this._refetchOnChange([
			'summaryType', 'groupCalendarId', 'breakdownCalendarId'
		], (prop, model, value, opts) => {
			const oldValue = model.previous(prop)
			// #40657 Going back and forth between value and cat/range will change the
			// result value sent down, (cat/range ID vs actual value), and thus
			// requires a refetch.
			const involvesRange = oldValue === 'range' || value === 'range'
			return !_.isEmpty(this.reportModel.groupingIdByFieldIdOrColumnType())
				|| _.includes([DATE_TIME, DATE], model.get('dataType'))
				|| involvesRange
		})

		//handle updates on the various nested collections that required refetching
		this.on('update', (collection, opts) => {
			//updates that come directly on the stack collection itself will not
			//have a collection name
			let { innerCollectionName } = opts
			if (_.isNil(innerCollectionName)) {
				this._onUpdateStackCollection(collection, opts)
			} else if (innerCollectionName == 'repeatBy') {
				this._onUpdateRepeatByCollection(collection, opts)
			} else if (innerCollectionName == 'columns') {
				this._onUpdateColumnCollection(collection, opts)
			}
		})
	}

	_onUpdateStackCollection(collection, opts) {
		//ensure the ordering is up to date
		_.each(collection.models, (stack,index) => stack.index = index)

		let {added, removed} = opts.changes
		//if the update was specified as transient, do nothing
		if (opts.transient) { return }

		//check to see if we need to refetch when adding a stack
		if (!_.isEmpty(added) && !this.refetch) {
			let columns = _.flatMap(opts.changes.added, stack => stack.columns().models)
			if (hasNoTransientModels(columns)) {
				//only one stack needs to require refetch
				if (_.find(opts.changes.added, stack => stack.requiresRefetch(this.reportModel))) {
					this.refetch = true
				}
			}
		}
	}

	_onUpdateColumnCollection(collection, opts) {
		let {added, removed, merged} = opts.changes
		//if we've added a new non-transient column in the repeat by, refetch
		if (opts.stack.isRepeatByStack() && !_.isEmpty(added)) {
			this.refetch = hasNoTransientModels(opts.changes.added)
		}
	}

	_onUpdateRepeatByCollection(collection, opts) {
		let {added, removed} = opts.changes
		//if this is a change on breakdowns of the repeatBy
		if (!_.isEmpty(added) && _.isEmpty(removed)) {
			//don't fetch if added transient stacks
			this.refetch = hasNoTransientModels(opts.changes.added)
		} else {
			this.refetch = true
		}
	}

	/**
	 * Setup a listener for change events for a given property that may require
	 * refetching data from the server
	 *
	 * @param {String|Array} property the property to listen for the change
	 * @param {Function} predicate return true if we should refetch data from
	 * the change
	 */
	_refetchOnChange(property, predicate = this._isPreviousNotEqual) {
		if (_.isArray(property)) {
			_.each(property, p => this._refetchOnChange(p, predicate))
			return
		}
		this.on(`change:${property}`, (model, value, opts) => {
			if (predicate(property, model, value, opts)) {
				this.refetch = true
			}
		})
	}

	_isPreviousNotEqual(property, model, newValue) {
		let previousValue = model.previous(property)
		return !_.isEqual(previousValue, newValue)
	}

	validate() {
		return _.flatMap(this.models, m => m.validate() || [])
	}

	parse(attrs, opts) {
		this._sortedColumn = undefined
		return _.map(attrs, (props, index) => {
			let stack = this.get(props)
			if (stack && opts.merge) {
				//if this is an existing stack, update the attributes
				stack.set(stack.parse(props, {silent: true}), {silent: true})
			} else {
				//otherwise create the new stack model
				stack = new ReportStack(props, {
					parse: true,
					reportDomainType: this.options.reportDomainType
				})
			}
			if (props.cid) {
				stack.cid = props.cid
			}
			stack.index = index
			//capture the initial columns that is sorted if any
			// #39093 We have to re-find this each time because our old reference will
			// go stale when we recreate column model objects in the above code.
			const currStackSortedColumn = stack.findColumnByAttribute((m) => {
				return m.has('sortType')
			})

			// #40656 Don't assign if no sorting column found in this stack.
			// Just because we aren't sorting on a column in this stack doesn't mean
			// we might not be sorting on a column in another stack.
			if (currStackSortedColumn) {
				this._sortedColumn = currStackSortedColumn
			}
			return stack
		})
	}

	/**
	 * Searches the columns in the stack for the given attribute/value pair,
	 * returns the column model and the index in the stack if found
	 *
	 * @param {Object} attribute
	 * @return {{index: number, model: ReportColumn}}
	 */
	findStackByColumnAttribute(attribute, opts = {}) {
		let index = -1
		let model = _.find(this.models, (stack, i) => {
			index = i
			return stack.findColumnByAttribute(attribute)
		})
		return model ? {index, model} : {}
	}

	/**
	 * Returns an array of stacks that have a breakdown (aka repeat by a column)
	 * or an empty array if none
	 */
	findStacksWithBreakdowns() {
		return _.filter(this.models, stack => {
			let repeatBy = stack.get('repeatBy')
			if (_.isNil(repeatBy)) { return false }
			return _.some(repeatBy.get('breakdowns') || [], breakdown =>
				breakdown.isBreakingDownWithColumn())
		})
	}

	warnings() {
		const tooManyCategories =
			i18n.get('report.designer.repeatByExceedsLimit.singular')
		const tooManyCategoriesPlural =
			i18n.get('report.designer.repeatByExceedsLimit.plural')

		const allWarnings = this.models.flatMap(it => it.warnings())
		const duplicatedWarnings = _(allWarnings)
			.countBy()
			.pickBy((count, _) => count > 1)
			.keys()
			.value()
		if (_.includes(duplicatedWarnings, tooManyCategories)) {
			const firstIdx = allWarnings.indexOf(tooManyCategories)
			allWarnings.splice(firstIdx, 1, tooManyCategoriesPlural)
			_.pull(allWarnings, tooManyCategories)
		}

		return allWarnings
	}

	_validateColumnState(reportColumnModel) {
		let isTreeSortType = reportColumnModel.get('sortType') == 'tree'
		let allowTreeSortType = !!reportColumnModel.get('supportsHierarchyOrdering')
		if (isTreeSortType & !allowTreeSortType) {
			reportColumnModel.unset('sortType')
		}
		let indentHierarchy = !!reportColumnModel.get('indentHierarchy')
		let allowIndentHierarchy = !!reportColumnModel.get('supportsIndentHierarchy')
		if (indentHierarchy & !allowIndentHierarchy) {
			reportColumnModel.unset('indentHierarchy')
		}
	}
}

export default Stacks
Stacks.__filename__ = 'reports/common/model/stacks.js'
